import { Get, Put, Post, Patch, Delete } from "./api.helper";
import { store } from "store";
import Config from "Config";
import { appendQueryParams } from "utils/urlUtils";
import { handleFetchError } from "../utils/errorHandler";

// ==== Authentication
export const Login = (bodyData) => {
  return Post({
    url: "/auth/login",
    body: bodyData,
    isAuthorized: false,
  });
};

export const GetMe = () => {
  return Get({ url: "/user/me" });
};

export const ForgotPassword = (bodyData) => {
  return Post({ url: "/auth/forget-password", body: bodyData });
};

export const ResendOTP = (bodyData) => {
  return Post({ url: "/auth/resend-otp", body: bodyData });
};

export const ForgotPasswordVerification = (bodyData) => {
  return Post({
    url: "/auth/forget-password/verification",
    body: bodyData,
  });
};

export const ResetPassword = (bodyData) => {
  return Post({ url: "/auth/reset-password", body: bodyData });
};

export const VerifyUser = (bodyData) => {
  return Post({
    url: "/",
    body: bodyData,
    isAuthorized: false,
  });
};

export const SignUp = (bodyData) => {
  return Post({
    url: "/",
    body: bodyData,
    isAuthorized: false,
  });
};

export const changePassword = (bodyData) => {
  return Post({
    url: "/auth/change-password",
    body: bodyData,
  });
};
// ALPHA LISTING API
export const customerListingApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/users", params, abortSignal });
};

export const userUpdateStatus = ({ id, bodyData }) => {
  return Post({
    url: `/user/${id}/status`,
    body: bodyData,
  });
};

// Grants/revokes one admin teammate's access to the standalone MCAP app —
// see Admin Access page. Target must already be an ADMIN-type account.
export const setMcapAccess = ({ id, mcapAccess }) => {
  return Post({
    url: `/user/${id}/mcap-access`,
    body: { mcapAccess },
  });
};

export const GetUserById = (id) => {
  return Get({
    url: `/user/${id}`,
  });
};

export const GetUserAffirmations = (params = {}, abortSignal = null) => {
  return Get({ url: "/affirmations", params, abortSignal });
};

// BLS FEEDBACK

export const analyticalDataAPI = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/summary", params, abortSignal });
};

export const userMetricsApi = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/user-metrics", params, abortSignal });
};

export const blsListing = (params = {}, abortSignal = null) => {
  return Get({ url: "/bls", params, abortSignal });
};

export const getUserMoods = (params = {}, abortSignal = null) => {
  return Get({ url: "/mood/moods", params, abortSignal });
};

export const getUserTests = (params = {}, abortSignal = null) => {
  return Get({ url: "/test/user-tests", params, abortSignal });
};

// BLS FEEDBACK FORMS

export const feedbackFormListing = (params = {}, abortSignal = null) => {
  return Get({ url: "/bls/feedback-forms", params, abortSignal });
};

export const createFeedbackForm = (bodyData) => {
  return Post({ url: "/bls/feedback-form", body: bodyData });
};

export const updateFeedbackForm = ({ id, bodyData }) => {
  return Patch({ url: `/bls/feedback-form/${id}`, body: bodyData });
};

export const deleteFeedbackForm = (id) => {
  return Delete({ url: `/bls/feedback-form/${id}` });
};

export const updateUserProfile = ({ id, bodyData }) => {
  return Patch({ url: `/user/${id}`, body: bodyData });
};

export const MediaUpload = (bodyData, abortSignal = null) => {
  const token = store.getState().userAuth.user.Token;

  const url = `${Config.env().API_URL}/media/upload`;

  return fetch(url, {
    headers: {
      authorization: token,
    },
    body: bodyData,
    method: "POST",
  }).then(handleFetchError);
};

// NOTIFICATIONS MANAGEMENT

export const createScheduledNotification = (bodyData) => {
  return Post({
    url: "/scheduled-notifications",
    body: bodyData,
  });
};

export const getScheduledNotifications = (params = {}, abortSignal = null) => {
  return Get({ url: "/scheduled-notifications", params, abortSignal });
};

export const getScheduledNotificationById = (id) => {
  return Get({
    url: `/scheduled-notifications/${id}`,
  });
};

export const updateScheduledNotification = ({ id, bodyData }) => {
  return Put({
    url: `/scheduled-notifications/${id}`,
    body: bodyData,
  });
};

export const deleteScheduledNotification = (id) => {
  return Delete({
    url: `/scheduled-notifications/${id}`,
  });
};

// AUDIO/VIDEO CONTENT LIBRARY

export const getAudioVideoContents = (params = {}, abortSignal = null) => {
  return Get({ url: "/audio-video-content", params, abortSignal });
};

export const getAudioVideoContentById = (id) => {
  return Get({ url: `/audio-video-content/${id}` });
};

export const createAudioVideoContent = (bodyData) => {
  return Post({ url: "/audio-video-content", body: bodyData });
};

export const updateAudioVideoContent = ({ id, bodyData }) => {
  return Patch({ url: `/audio-video-content/${id}`, body: bodyData });
};

// Soft delete — moves it to the bin (status=BIN in getAudioVideoContents).
export const deleteAudioVideoContent = (id) => {
  return Delete({ url: `/audio-video-content/${id}` });
};

export const restoreAudioVideoContent = (id) => {
  return Post({ url: `/audio-video-content/${id}/restore` });
};

// Hard delete — only allowed once the item is already in the bin; the
// backend rejects this (with a message surfaced automatically by
// handleFetchError's toast) if the content has real play/favorite history.
export const permanentlyDeleteAudioVideoContent = (id) => {
  return Delete({ url: `/audio-video-content/${id}/permanent` });
};

// LOCKED SESSION CONTENT — Calm (Micro Reset) and Focus (breathing), the two
// built-in sessions with no AudioVideoContent row of their own. stateKey is
// always "calm" or "focus".
export const getLockedSessionContent = (stateKey) => {
  return Get({ url: `/locked-session-content/${stateKey}` });
};

export const updateLockedSessionContent = ({ stateKey, bodyData }) => {
  return Patch({ url: `/locked-session-content/${stateKey}`, body: bodyData });
};

export const getExportUserListing = (params = {}, abortSignal = null) => {
  const token = store.getState().userAuth.user.Token;
  const url = `${Config.env().API_URL}/users/export`;

  const queryString = new URLSearchParams(params).toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  return fetch(fullUrl, {
    method: "GET",
    headers: {
      Authorization: token,
    },
    signal: abortSignal,
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  });
};

// CLICK ANALYTICS

export const getClickAnalyticsListing = (params = {}, abortSignal = null) => {
  return Get({ url: "/deep-link-analytics", params, abortSignal });
};

export const getClickAnalyticsDetailsByDate = (params) => {
  return Get({ url: "/deep-link-analytics/details", params });
};

// MCAP — Mind Circuit Analytics Platform

// ---- Section A: org totals, adoption ------------------------------------
export const getMcapOrgTotals = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/org-totals", params, abortSignal });
};

export const getMcapAdoptionMetrics = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/adoption-metrics", params, abortSignal });
};

export const getMcapAdoptionFlags = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/adoption-flags", params, abortSignal });
};

// ---- Section B: implementation, settings, school view --------------------
// MCAP settings — the administrator-tunable assumptions behind published
// figures (off-task baseline, multi-setting threshold, aggregation floor).
// GET always returns values: the backend upserts defaults on first read, so
// there is no "not configured yet" state to handle here.
export const getMcapSettings = (abortSignal = null) => {
  return Get({ url: "/analytics/mcap-settings", abortSignal });
};

// Partial update — send only the fields being changed; omitted ones keep
// their stored value.
export const updateMcapSettings = (bodyData) => {
  return Put({ url: "/analytics/mcap-settings", body: bodyData });
};

// Implementation fidelity + instructional time. Every rate field can come
// back null, meaning "not enough data to report" — never zero. The UI must
// distinguish the two.
export const getMcapImplementationSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/implementation-summary", params, abortSignal });
};

// The classrooms behind one reset moment — opened from a row on the moment
// table. Takes the dashboard's own filters plus `context`.
export const getMcapImplementationMoment = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/implementation-moment", params, abortSignal });
};

// Classrooms using resets at exactly one moment — the list behind the
// "needs support" tile.
export const getMcapImplementationSingleMoment = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/implementation-single-moment", params, abortSignal });
};

// Schools ranked by resets in the window — what the School view picker shows
// before the admin types anything.
export const getMcapRankedSchools = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/schools/ranked", params, abortSignal });
};

// The extra detail one school earns: playback funnel + per-activity breakdown.
export const getMcapSchoolView = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/school-view", params, abortSignal });
};

// The printable summary. Fetched as a blob rather than opened in a new tab
// because the endpoint needs an Authorization header, which a plain
// window.open cannot send.
export const getMcapImplementationReport = (params = {}) => {
  const token = store.getState().userAuth.user.Token;
  const queryString = new URLSearchParams(params).toString();
  const url = `${Config.env().API_URL}/analytics/implementation-report${queryString ? `?${queryString}` : ""}`;

  return fetch(url, { method: "GET", headers: { Authorization: token } }).then((response) => {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  });
};

export const getMcapEngagementSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/engagement/summary", params, abortSignal });
};

// Overview's own engagement widget — role:Educator-scoped, so it never
// blends in the separate mobile companion app's own users the way the
// generic engagement/summary above can at platform-wide scope.
export const getMcapTeacherEngagement = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/teacher-engagement", params, abortSignal });
};

export const getMcapWellbeingSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/wellbeing/summary", params, abortSignal });
};

export const getMcapContentSummary = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/summary", params, abortSignal });
};

export const getMcapStateTrends = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/state-trends", params, abortSignal });
};

export const getMcapUsageTrends = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/usage-trends", params, abortSignal });
};

export const getMcapActivityRanking = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/content/activity-ranking", params, abortSignal });
};

export const getMcapLicensingSummary = (abortSignal = null) => {
  return Get({ url: "/analytics/licensing/summary", abortSignal });
};

export const getMcapTimeOfDay = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/time-of-day", params, abortSignal });
};

export const getMcapScreenViews = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/screen-views", params, abortSignal });
};

export const getMcapSectionSummary = (sectionId) => {
  return Get({ url: `/analytics/org/section/${sectionId}` });
};

export const getMcapSchoolOptions = () => {
  return Get({ url: "/analytics/org/schools" });
};

export const getMcapDashboard = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/dashboard", params, abortSignal });
};

export const getMcapExportUrl = (params = {}) => {
  const token = store.getState().userAuth.user.Token;
  const url = `${Config.env().API_URL}/analytics/export`;
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  return fetch(fullUrl, {
    method: "GET",
    headers: { Authorization: token },
  }).then((response) => {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  });
};

// ---- MCAP Payments & Licensing ---------------------------------------------
//
// The row-level calls below return names, emails and payment details. They are
// never cached client-side (see the licensing tab's own query hook) and the
// server sends them with Cache-Control: no-store.

export const getMcapLicensingOverview = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/licensing/overview", params, abortSignal });
};

export const getMcapLicensingSchools = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/licensing/schools", params, abortSignal });
};

export const getMcapLicensingCohort = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/licensing/cohort", params, abortSignal });
};

export const getMcapLicensingPayments = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/licensing/payments", params, abortSignal });
};

export const getMcapLicensingActivity = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/licensing/activity", params, abortSignal });
};

export const getMcapLicensingActivitySchools = (params = {}, abortSignal = null) => {
  return Get({ url: "/analytics/licensing/activity/schools", params, abortSignal });
};

// A CSV of exactly the rows a drill-down is showing. Same fetch shape as
// getMcapExportUrl above: the token goes in a header, never in the URL.
export const getMcapLicensingExport = (params = {}) => {
  const token = store.getState().userAuth.user.Token;
  const url = `${Config.env().API_URL}/analytics/licensing/export`;
  const queryString = new URLSearchParams(params).toString();

  return fetch(queryString ? `${url}?${queryString}` : url, {
    method: "GET",
    headers: { Authorization: token },
  }).then((response) => {
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  });
};

// ---- WhatsApp inbox ----------------------------------------------------------
//
// Lead phone numbers and message text: never cached client-side. Behind
// inboxAccess on the server (RequireInboxAccess), separately from mcapAccess.

export const getInboxConversations = (params = {}, abortSignal = null) => {
  return Get({ url: "/mcap/whatsapp/conversations", params, abortSignal });
};

export const getInboxConversation = (id, abortSignal = null) => {
  return Get({ url: `/mcap/whatsapp/conversations/${id}`, abortSignal });
};

export const getInboxMessages = (id, params = {}, abortSignal = null) => {
  return Get({ url: `/mcap/whatsapp/conversations/${id}/messages`, params, abortSignal });
};

export const takeOverInboxConversation = (id) => {
  return Post({ url: `/mcap/whatsapp/conversations/${id}/takeover` });
};

export const handBackInboxConversation = (id) => {
  return Post({ url: `/mcap/whatsapp/conversations/${id}/handback` });
};

// clientMessageId makes a double-click or a retry after a timeout send once.
export const replyToInboxConversation = (id, message, clientMessageId) => {
  return Post({ url: `/mcap/whatsapp/conversations/${id}/reply`, body: { message, clientMessageId } });
};

// Live inbox stream (Server-Sent Events). EventSource cannot send a header, so
// the stream is opened with a single-use 60-second ticket, never the session
// token (which would land in access logs).
export const getInboxStreamTicket = () => {
  return Post({ url: "/mcap/whatsapp/stream-ticket" });
};

export const inboxStreamUrl = (ticket) =>
  `${Config.env().API_URL}/mcap/whatsapp/stream?ticket=${encodeURIComponent(ticket)}`;
